import React from 'react'

/** Same copy as Investment Essentials modal — inline for invest-v2 (no modal). */
const InvestingRisksDocumentBody = () => (
  <div className='invest-v2__ie-doc-body'>
    <h2 className='invest-v2__ie-doc-title'>Investment Risks</h2>
    <p>
      Investing in today&apos;s dynamic market landscape can be a rewarding yet challenging
      endeavour. For both new and seasoned investors, understanding the myriad of risks involved is
      crucial to making informed decisions. The market&apos;s inherent volatility means that prices
      can fluctuate rapidly, influenced by a variety of factors.
    </p>
    <p>
      <strong>Currency Risk:</strong> Involves the potential loss from changes in exchange rates,
      affecting international investments. For example, an investor holding European assets may see
      their returns diminished if the euro depreciates against the dollar.
    </p>
    <p>
      <strong>Inflation Risk:</strong> Rising prices can erode the purchasing power of returns over
      time. An investment yielding 3% annually may effectively lose value if inflation is at 4%.
    </p>
    <p>
      <strong>Liquidity Risk:</strong> The challenge associated with converting an investment into
      cash without significant loss of value. For example, real estate investments may take months
      to sell without incurring a loss.
    </p>
    <p>
      <strong>Credit Risk:</strong> The possibility that a borrower may default on their
      obligations, impacting the client&apos;s returns. A classic instance is when a company fails
      to pay interest on its bonds, affecting bondholders.
    </p>
    <p>
      <strong>Concentration Risk:</strong> Arises when a portfolio lacks diversification,
      potentially leading to significant losses if one sector or investment performs poorly, such as
      during the dot-com bubble burst when tech-heavy portfolios suffered major declines.
    </p>
    <p>
      Other risks to consider include:
      <br />- <strong>Default Risk:</strong> When an issuer fails to make required payments.
      <br />- <strong>Reinvestment Risk:</strong> Occurs when future cash flows, such as coupon
      payments, are reinvested at a lower interest rate.
      <br />- <strong>Interest Rate Risk:</strong> Pertains to the impact of fluctuating rates on
      the value of investments, especially bonds.
      <br />- <strong>Longevity Risk:</strong> A unique challenge for retirement planning, where
      there&apos;s a concern about outliving one&apos;s financial resources.
    </p>
    <p>
      Additionally, changes in regulations or government policies can significantly impact
      investment outcomes. For instance, new environmental laws could affect profitability.
    </p>
    <h3 className='invest-v2__ie-doc-subtitle'>Are there any Guarantees?</h3>
    <p>
      Investing in the ever-changing market can be both exciting and daunting for investors. While
      the potential for growth is significant, it&apos;s natural to wonder whether there are any
      guarantees regarding the protection of your capital. It&apos;s important to understand that
      the market, by its very nature, is unpredictable and subject to fluctuations. However, there
      are strategies and considerations that can help manage risks and safeguard investments.
    </p>
    <p>
      <strong>Diversification:</strong> A key strategy in managing investment risk. By spreading
      investments across different asset classes, industries, or geographies, investors can reduce
      the impact of any single market downturn on their overall portfolio. While it doesn&apos;t
      guarantee against loss, it can provide a buffer against volatility.
    </p>
    <p>
      <strong>Time Horizon:</strong> Markets tend to experience cycles of ups and downs, and while
      short-term fluctuations can be unsettling, a long-term approach often smooths out these
      variations. Historical data suggests that the longer you remain invested, the greater the
      likelihood of achieving positive returns.
    </p>
    <p>
      <strong>Risk Tolerance:</strong> Understanding your appetite for risk is crucial. Aligning
      your investment choices with your comfort level is essential. For example, government bonds
      offer more stability than stocks, which might be more volatile but offer higher potential
      returns.
    </p>
    <p>
      While no investment is entirely without risk, working with a financial advisor can provide
      guidance tailored to your individual goals and circumstances. Advisors can help craft a
      balanced portfolio that aligns with your objectives while considering the inherent risks in
      the market.
    </p>
    <p>
      <strong>In Conclusion:</strong> While there are no absolute guarantees in investing, being
      informed and strategic can help protect your capital. By diversifying, understanding your risk
      tolerance, and adopting a long-term perspective, you can navigate the market&apos;s
      uncertainties with greater confidence.
    </p>
  </div>
)

type InvestV2InvestmentRisksInlineProps = {
  acknowledged: boolean
  onCheckedChange: (checked: boolean) => void | Promise<void>
  isSubmitting?: boolean
}

const InvestV2InvestmentRisksInline = ({
  acknowledged,
  onCheckedChange,
  isSubmitting = false,
}: InvestV2InvestmentRisksInlineProps) => {
  return (
    <div className='invest-v2__ie-inline' data-testid='invest-v2-investment-risks-inline'>
      <h2 className='invest-v2__risk-title'>
        <span className='invest-v2__risk-icon' aria-hidden>
          📄
        </span>
        Investment Risks
      </h2>
      <details className='invest-v2__ie-details'>
        <summary className='invest-v2__ie-details-summary'>View full document</summary>
        <div className='invest-v2__ie-details-panel'>
          <InvestingRisksDocumentBody />
        </div>
      </details>
      <label className='invest-v2__check invest-v2__check--ie'>
        <input
          type='checkbox'
          checked={acknowledged}
          disabled={isSubmitting}
          onChange={(e) => {
            void onCheckedChange(e.target.checked)
          }}
          data-testid='invest-v2-investment-essentials-ack'
        />
        <span>I confirm that I have read the Investment Risks and accept the same.</span>
      </label>
    </div>
  )
}

export { InvestV2InvestmentRisksInline, InvestingRisksDocumentBody }
